import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { useChat } from './hooks/useChat';
import { useInquiryHistory } from './hooks/useInquiryHistory';
import { useTypingEffect } from './hooks/useTypingEffect';
import { useInquiryStatusPageEvents } from './hooks/useInquiryStatusPageEvents';

import InquiryStatusLayout from './components/layout/InquiryStatusLayout';
import InquiryTabContent from './components/layout/InquiryTabContent';
import ChatInputBar from './components/chat/ChatInputBar';
import InquiryStatusModals from './components/history/InquiryStatusModals';
import { AuthContext } from '../../context/AuthContext';

/**
 * 질의 현황 관리 메인 페이지.
 * 채팅, 질의 내역, FAQ 탭을 관리하며 전체적인 레이아웃 및 훅을 오케스트레이션합니다.
 */
const InquiryStatusPage = () => {
    const { auth } = useContext(AuthContext);

    // 1. 현재 사용자 정보 (AuthContext에서 동적 매핑)
    const CURRENT_USER = useMemo(() => ({
        id: auth?.userId || 'user_001',
        name: auth?.name || '익명 사용자',
        role: auth?.roleId || auth?.role || 'R2',
        company: auth?.compCode || 'UNKNOWN'
    }), [auth?.userId, auth?.name, auth?.roleId, auth?.role, auth?.compCode]); // 이 값들이 바뀔 때만 새로 만듦
    // 권한 코드(AI_SCOPE_*)로 뷰 모드 계산
    const calcViewMode = (perms = []) =>
        perms.includes('AI_SCOPE_ALL') ? 'admin'
        : perms.includes('AI_SCOPE_COMPANY') ? 'company'
        : 'personal';

    // 2. 핵심 상태 및 훅 초기화
    const [activeTab, setActiveTab] = useState('chat');
    const [viewMode, setViewMode] = useState(() => calcViewMode(auth?.permissions));

    // auth.permissions 갱신 시 viewMode 자동 동기화
    useEffect(() => {
        setViewMode(calcViewMode(auth?.permissions));
    }, [auth?.permissions]);
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
    const [inputText, setInputText] = useState('');
    const messagesEndRef = useRef(null);

    // 서비스 훅 (작업 내역, 채팅, 타이핑 효과)
    const { 
        messages, 
        isAiTyping, 
        isSessionLoading,
        sendMessage, 
        historyList, 
        isHistoryLoading, 
        fetchHistoryList, 
        startNewChat 
    } = useChat(CURRENT_USER, viewMode);
    
    const historyProps = useInquiryHistory(viewMode, CURRENT_USER, historyList, isHistoryLoading);
    const { completedTypingIds, handleComplete, resetTyping } = useTypingEffect();

    // 3. 페이지 전용 이벤트 핸들러 및 상태 (삭제, 전송, 토스트)
    const {
        deleteTargetId,
        setDeleteTargetId,
        toastState,
        hideToast,
        handleDeleteClick,
        handleConfirmDelete,
        handleSend
    } = useInquiryStatusPageEvents(
        historyProps,
        sendMessage,
        fetchHistoryList,
        inputText,
        setInputText,
        isAiTyping,
        activeTab,
        CURRENT_USER,
        viewMode
    );

    // 모바일 등 외부 이벤트 대응
    useEffect(() => {
        const handler = () => setIsSidebarOpen(false);
        window.addEventListener('layout-sidebar-open', handler);
        return () => window.removeEventListener('layout-sidebar-open', handler);
    }, []);

    // 최초 렌더링 및 모드 변경 시 내역 로드
    useEffect(() => {
        fetchHistoryList(CURRENT_USER.id || 'guest', viewMode);
    }, [fetchHistoryList, viewMode, CURRENT_USER.id]);

    // 사이드바용 탭 전환 핸들러 (모바일 자동 닫기 포함)
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (window.innerWidth < 768) setIsSidebarOpen(false);
    };

    return (
        <InquiryStatusLayout
            isSidebarOpen={isSidebarOpen}
            onToggleSidebar={() => setIsSidebarOpen(prev => !prev)}
            activeTab={activeTab}
            setActiveTab={handleTabChange}
            viewMode={viewMode}
            setViewMode={setViewMode}
            currentUser={CURRENT_USER}
            recentInquiries={historyProps.recentInquiries}
            isHistoryLoading={isHistoryLoading}
            onSelectInquiry={(inquiry) => {
                historyProps.setSelectedInquiry(inquiry);
                if (window.innerWidth < 768) setIsSidebarOpen(false);
            }}
            onDeleteInquiry={handleDeleteClick}
            onNewChat={() => {
                startNewChat();
                resetTyping();
                if (window.innerWidth < 768) setIsSidebarOpen(false);
            }}
        >
            {/* 중앙 콘텐츠 영역 (탭 전환 로직) */}
            <InquiryTabContent
                activeTab={activeTab}
                viewMode={viewMode}
                currentUser={CURRENT_USER}
                messages={messages}
                isAiTyping={isAiTyping}
                isSessionLoading={isSessionLoading}
                messagesEndRef={messagesEndRef}
                completedTypingIds={completedTypingIds}
                handleComplete={handleComplete}
                historyProps={historyProps}
                onDeleteInquiry={handleDeleteClick}
                onSelectInquiry={historyProps.setSelectedInquiry}
            />

            {/* 채팅 입력바 (채팅 탭일 때만 하단 레이아웃 내에 표시) */}
            {activeTab === 'chat' && (
                <ChatInputBar
                    inputText={inputText}
                    setInputText={setInputText}
                    onSend={handleSend}
                    isAiTyping={isAiTyping}
                />
            )}

            {/* 공통 모달 레이어 */}
            <InquiryStatusModals
                selectedInquiry={historyProps.selectedInquiry}
                onCloseDetail={() => historyProps.setSelectedInquiry(null)}
                deleteTargetId={deleteTargetId}
                onCloseDelete={() => setDeleteTargetId(null)}
                onConfirmDelete={handleConfirmDelete}
                toastState={toastState}
                onCloseToast={hideToast}
            />
        </InquiryStatusLayout>
    );
};

export default InquiryStatusPage;
